import { useLayoutEffect, useSyncExternalStore } from 'react';
import { repaintBrandForTheme } from './branding';

export type Theme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'his.theme';

const listeners = new Set<() => void>();
let preferred: Theme = readStoredTheme();
let forcedDarkHolders = 0;

function readStoredTheme(): Theme {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function paint() {
  const root = document.documentElement;
  const dark = forcedDarkHolders > 0 || preferred === 'dark';
  if (root.classList.contains('dark') === dark) return;
  root.classList.add('theme-switching');
  root.classList.toggle('dark', dark);
  repaintBrandForTheme();
  requestAnimationFrame(() => requestAnimationFrame(() => root.classList.remove('theme-switching')));
}

function notify() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function storeTheme(theme: Theme) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
    return true;
  } catch {
    return false;
  }
}

export function setTheme(theme: Theme) {
  preferred = theme;
  storeTheme(theme);
  paintAndNotify();
}

function paintAndNotify() {
  paint();
  notify();
}

export function followThemeAcrossTabs() {
  window.addEventListener('storage', (event) => {
    if (event.key !== THEME_STORAGE_KEY && event.key !== null) return;
    preferred = readStoredTheme();
    paintAndNotify();
  });
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, () => preferred);
  return {
    theme,
    dark: theme === 'dark',
    toggle: () => setTheme(theme === 'dark' ? 'light' : 'dark'),
  };
}

export function useForcedDark() {
  useLayoutEffect(() => {
    forcedDarkHolders += 1;
    paint();
    return () => {
      forcedDarkHolders -= 1;
      paint();
    };
  }, []);
}
